import { Injectable } from '@angular/core';
import { TvDashboardFacade, AppMasterDBFacade } from '@monorepo/core-data';
import {
  TComponentNames,
  TDashboardComponentDescriptor,
  TComponentMessageMap,
  TTopicArgsContext,
  TDashboardMinimalStationInfo,
} from '@monorepo/types';
import { Md5 } from 'ts-md5';
import { Observable, EMPTY } from 'rxjs';
import { map, take, switchMap, filter } from 'rxjs/operators';
import { TvDashboardWebSocketService } from './tv-dashboard-websocket.service';

/**
 * Service responsible for handling TV dashboard utilities and WebSocket connections.
 * Manages component data processing and WebSocket communication for various dashboard widgets.
 */
@Injectable({
  providedIn: 'root',
})
export class TvDashboardUtilService {
  constructor(
    private tvDashboardFacade: TvDashboardFacade,
    private appMasterDBFacade: AppMasterDBFacade,
    private webSocketService: TvDashboardWebSocketService,
  ) {}

  /**
   * Processes topic arguments by handling special date tokens and subject-related properties.
   * @param args - Topic arguments to process
   * @param argsContext - Additional context to process topic args
   * @returns Processed topic arguments with resolved date tokens and subject information
   * @private
   */
  private preprocessTopicArgs(
    args: TDashboardComponentDescriptor['data_model_args']['topic_args'],
    argsContext: TTopicArgsContext,
  ) {
    const processedArgs: TDashboardComponentDescriptor['data_model_args']['topic_args'] = JSON.parse(
      JSON.stringify(args),
    );

    processedArgs.forEach((arg) => {
      if ('$ct' in arg) {
        arg.$ct = this.#getCurrentDate();
      }
      if ('$dst' in arg) {
        arg.$dst = this.#getCurrentDateStart();
      }

      if (!arg.subject_fixed && arg.subject) {
        // Note: You'll need to get the current subject from your app context
        // arg.subject = currentSubject.id;
        arg['subject'] = argsContext.subject;

        if (arg.dr_properties) {
          // Add interval processing if needed
          this.interval(arg.dr_properties);
        }
      }

      arg['act_tkn'] = argsContext.activationToken;
      arg['appmasterid'] = argsContext.appMasterId;
      arg['app_version'] = 'v2';
      arg['module_id'] = argsContext.moduleId;
    });

    return processedArgs;
  }

  /**
   * Processes interval properties for date ranges.
   * Handles different window frames (month/day) and formats dates accordingly.
   * @param props - Properties containing window frame information
   * @private
   */
  private interval(props: TDashboardComponentDescriptor['data_model_args']['topic_args'][number]['dr_properties']) {
    const today = new Date();
    if (props?.window) {
      if (props.window === 'window_frame_month') {
        const start_month = new Date(today.getFullYear(), today.getMonth(), 1);
        const end_month = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const first_date =
          start_month.getFullYear() +
          '-' +
          this.changeDate(start_month.getMonth() + 1) +
          '-' +
          this.changeDate(start_month.getDate());
        const last_date =
          end_month.getFullYear() +
          '-' +
          this.changeDate(end_month.getMonth() + 1) +
          '-' +
          this.changeDate(end_month.getDate());

        props.window = first_date + '/' + last_date;
      } else if (props.window === 'window_frame_day') {
        const start =
          today.getFullYear() + '-' + this.changeDate(today.getMonth() + 1) + '-' + this.changeDate(today.getDate());
        const end =
          today.getFullYear() + '-' + this.changeDate(today.getMonth() + 1) + '-' + this.changeDate(today.getDate());
        props.window = start + '/' + end;
      }
    }
  }

  /**
   * Formats a date number by adding leading zero if necessary.
   * @param date - Number representing a date or month
   * @returns Formatted date string with leading zero if needed
   * @private
   */
  private changeDate(date: number) {
    if (date < 10) {
      return '0' + date;
    } else {
      return date;
    }
  }

  /**
   * Gets current date information including timestamp and timezone offset.
   * @param date - Optional date to process instead of current date
   * @returns Object containing timestamp and timezone offset
   * @private
   */
  #getCurrentDate(date?: any) {
    const d = date ? new Date(date) : new Date();
    return {
      ts: d.getTime(),
      offset: d.getTimezoneOffset(),
    };
  }

  /**
   * Gets start of current date information including timestamp and timezone offset.
   * @param date - Optional date to process instead of current date
   * @returns Object containing timestamp and timezone offset for start of day
   * @private
   */
  #getCurrentDateStart(date?: any) {
    const d = date ? new Date(date) : new Date();
    d.setHours(0);
    d.setMinutes(1);
    return {
      ts: d.getTime(),
      offset: d.getTimezoneOffset(),
    };
  }

  /**
   * Sets up WebSocket connection for a dashboard component and handles data streaming.
   * @param config - Dashboard component configuration
   * @param componentName - Name of the component to set up
   * @param subjectKey - Subject key for the WebSocket connection
   * @returns Observable stream of component-specific messages
   * @template CN - Component name type constraint
   */
  setupWebSocketConnection<CN extends TComponentNames>(
    config: TDashboardComponentDescriptor,
    componentName: CN,
    subjectKey: string,
    stationInfo: TDashboardMinimalStationInfo,
  ): { msgData$: Observable<TComponentMessageMap[CN]> } {
    // Create a single observable chain
    const msgData$ = this.tvDashboardFacade.getWebsocketConnectionStatus().pipe(
      // Only proceed when connected
      map((status) => status === 'CONNECTED'),
      switchMap((isConnected) =>
        this.appMasterDBFacade.getTokenAndSelectedSubject().pipe(
          take(1),
          map((tokenAndSubjectInfo) => ({ isConnected, tokenAndSubjectInfo })),
        ),
      ),
      // When both are ready, set up the channel and send data
      map(({ isConnected, tokenAndSubjectInfo }) => {
        if (isConnected && tokenAndSubjectInfo) {
          const processedTopicArgs = this.preprocessTopicArgs(config.data_model_args.topic_args, {
            subject: stationInfo.subject,
            activationToken: tokenAndSubjectInfo.activationToken,
            appMasterId: tokenAndSubjectInfo.appMasterId,
            moduleId: stationInfo.dis_name,
          });
          const channelKey = Md5.hashStr(config.data_model_args.topic + JSON.stringify(processedTopicArgs));
          this.webSocketService.registerChannel(channelKey);
          this.tvDashboardFacade.mapChannelToComponent(channelKey, componentName);
          this.tvDashboardFacade.sendDataToWebsocket({
            componentName: componentName,
            message: {
              access_type: 'SUBSCRIBE',
              channel_id: channelKey,
              topic: config.data_model_args.topic,
              topic_args: processedTopicArgs,
              subject_key: subjectKey,
            },
          });

          return { isReady: true as const, channelKey: channelKey };
        }
        return { isReady: false as const, channelKey: undefined };
      }),
      // Only continue if we have both connection and subject
      switchMap(
        ({ isReady, channelKey }) =>
          isReady ? this.tvDashboardFacade.getComponentDataByChannelId(componentName, channelKey) : EMPTY, // No emissions when not ready
      ),
      filter((data): data is TComponentMessageMap[CN] => data !== undefined),
    );

    return { msgData$ };
  }
}
